import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Button, IconButton, Tooltip } from "@mui/material";
import { dateFormat } from "../../helpers/utilities";
import { encrypt } from "../../helpers/utilities";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import moment from "moment";
import "../../assets/scss/tables.scss";

/* The name lives on the team record, and the older `projectName` is kept as a
   fallback. Reading it in one place stops the table and the popup disagreeing. */
export const projectNameOf = (project) =>
  project?.projectTeam?.[0]?.projectName || project?.projectName || "--";

/* The project record itself is joined in as an array of one. Everything about
   the project — its name, its dates, its flags — is read through here. */
const masterOf = (project) => project?.projectTeam?.[0] || {};

export const projectManagers = (project) =>
  (project?.managerList || [])
    .map((manager) => manager?.employeeName)
    .filter(Boolean)
    .join(", ");

/* Closed either because someone marked it so, or because its end date passed. */
export const isProjectRunning = (project) => {
  const master = masterOf(project);
  if (master.isCompleted) return false;
  if (!master.projectEndDate) return true;
  return !moment(master.projectEndDate).isBefore(new Date(), 'day');
};

/* an entry only counts as a person once it carries a name */
const namedTeam = (project) =>
  (project?.employeeList || []).filter((row) => row && row.employeeName);

const ProjectsTable = ({ projectList }) => {
  const navigate = useNavigate();

  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [show, setShow] = useState(false);

  const [teamMem, setTeamMem] = useState({
    team: [],
    projectName: ""
  });

  const handleClose = () => setShow(false);

  const getPageNo = (data) => {
    setPageNumber(data)
  }

  /* a narrower list can leave you past the last page */
  useEffect(() => {
    setPageNumber(1)
  }, [projectList])

  return (
    <div className="table-responsive tab-styles">
      <table className="table">
        <thead>
          <tr>
            <th scope="col" className="wh-col-shrink">Project ID</th>
            <th scope="col">Project Name</th>
            <th scope="col">Project Manager</th>
            <th scope="col" className="wh-col-shrink">Start Date</th>
            <th scope="col" className="wh-col-shrink">Status</th>
            <th scope="col" className="wh-col-shrink">Team</th>
            <th scope="col" className="wh-col-shrink text-center">Reports</th>
          </tr>
        </thead>
        <tbody>
          {projectList && projectList.slice((pageNumber - 1) * rowsPerPage, pageNumber * rowsPerPage).map((project) => {

            const team = namedTeam(project);
            const name = projectNameOf(project);
            const managers = projectManagers(project);
            const running = isProjectRunning(project);

            return (
              <tr key={project.projectID}>
                <td className="wh-col-shrink">{project.projectID}</td>
                <td>{name}</td>
                <td>{managers || "--"}</td>
                <td className="wh-col-shrink">
                  {/* the dates live on the joined project record, not the team one */}
                  {masterOf(project).projectStartDate
                    ? dateFormat(masterOf(project).projectStartDate) : "--"}
                </td>
                <td className="wh-col-shrink">
                  <span className={`wh-pill ${running ? 'wh-pill-good' : 'wh-pill-quiet'}`}>
                    {running ? "Running" : "Completed"}
                  </span>
                </td>
                <td className="wh-col-shrink">
                  <Button
                    variant="outlined"
                    startIcon={<GroupsOutlinedIcon />}
                    disabled={team.length === 0}
                    onClick={() => {
                      setTeamMem({ team, projectName: name });
                      setShow(true);
                    }}
                  >
                    Team ({team.length})
                  </Button>
                </td>
                <td className="wh-col-shrink text-center">
                  <Tooltip title="View project reports" placement="top" arrow>
                    <IconButton onClick={() => {
                      /* the page it opens shows the name and "x of y submitted",
                         which it cannot work out on its own */
                      navigate(`/report-view/${encrypt("projectID")}/${encrypt(project.projectID)}`,
                        { state: { title: name, total: team.length } });
                    }}>
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {projectList && projectList.length > 0 ?
        <PaginationGlobal
          count={Math.ceil(projectList.length / rowsPerPage)}
          pageNumber={pageNumber} func={getPageNo}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(size) => { setRowsPerPage(size); setPageNumber(1); }} /> : ""}

      <Modal show={show} onHide={handleClose} size="lg" className="wh-modal">
        <Modal.Header closeButton>
          <Modal.Title>{teamMem.projectName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="wh-section-title" style={{ marginTop: 0 }}>
            <GroupsOutlinedIcon />Project Team Members
          </h6>

          {teamMem.team.length === 0
            ? <p className="wh-section-empty">Nobody is assigned to this project yet.</p>
            : <div className="table-responsive wh-table-shell">
              <table className="table">
                <thead>
                  <tr>
                    <th className="wh-col-shrink">Emp. ID</th>
                    <th>Name</th>
                    <th>Designation</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMem.team.map((each, index) => <tr key={index}>
                    <td className="wh-col-shrink">{each.employeeCode}</td>
                    <td>{each.employeeName}</td>
                    <td>{each.designation}</td>
                  </tr>)}
                </tbody>
              </table>
            </div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="contained" color="inherit" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectsTable;
